import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", fn, { passive: true });
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function toStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Returns 0=Mon … 6=Sun
function firstWeekday(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function formatShort(str) {
  if (!str) return "";
  const d = new Date(str + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function addMonths(year, month, n) {
  const d = new Date(year, month + n, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export default function DateRangePicker({ checkIn, checkOut, onChange, variant = "hero" }) {
  const isCard = variant === "card";
  const isMobile = useIsMobile();
  const today = new Date().toISOString().split("T")[0];
  const now = new Date();

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [selecting, setSelecting] = useState("start"); // "start" | "end"
  const [leftYear, setLeftYear] = useState(now.getFullYear());
  const [leftMonth, setLeftMonth] = useState(now.getMonth());

  const ref = useRef(null);
  const right = addMonths(leftYear, leftMonth, 1);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  function handleDayClick(dateStr) {
    if (dateStr < today) return;

    if (selecting === "start" || (checkIn && checkOut)) {
      onChange({ checkIn: dateStr, checkOut: "" });
      setSelecting("end");
    } else {
      if (dateStr <= checkIn) {
        onChange({ checkIn: dateStr, checkOut: "" });
        setSelecting("end");
      } else {
        onChange({ checkIn, checkOut: dateStr });
        setSelecting("start");
        setTimeout(() => setOpen(false), 120);
      }
    }
  }

  function isInRange(dateStr) {
    if (!checkIn) return false;
    const end = (selecting === "end" && hovered) ? hovered : checkOut;
    if (!end || end <= checkIn) return false;
    return dateStr > checkIn && dateStr < end;
  }

  function prev() {
    const p = addMonths(leftYear, leftMonth, -1);
    setLeftYear(p.year); setLeftMonth(p.month);
  }
  function next() {
    const n = addMonths(leftYear, leftMonth, 1);
    setLeftYear(n.year); setLeftMonth(n.month);
  }

  function renderMonth(year, month) {
    const total = daysInMonth(year, month);
    const offset = firstWeekday(year, month);
    const cells = [];

    for (let i = 0; i < offset; i++) cells.push(<div key={`pad-${i}`} />);

    for (let d = 1; d <= total; d++) {
      const ds = toStr(year, month, d);
      const past = ds < today;
      const isStart = ds === checkIn;
      const isEnd = ds === checkOut;
      const inRange = isInRange(ds);
      const isHov = ds === hovered && !past;

      let bg = "";
      let text = past ? "text-gray-300 cursor-default" : "text-[#1C1C1A] cursor-pointer";
      let rounded = "rounded-full";

      if (isStart || isEnd) {
        bg = "bg-[#E8970A]";
        text = "text-white font-semibold";
      } else if (inRange) {
        bg = "bg-[#FEF3E0]";
        rounded = "";
        if (isHov) bg = "bg-[#FDEBC0]";
      } else if (isHov) {
        bg = "bg-gray-100";
      }

      cells.push(
        <button
          key={ds}
          type="button"
          disabled={past}
          onClick={() => handleDayClick(ds)}
          onMouseEnter={() => !past && setHovered(ds)}
          onMouseLeave={() => setHovered(null)}
          className={`w-9 h-9 text-sm flex items-center justify-center transition-all select-none ${bg} ${text} ${rounded}`}
        >
          {d}
        </button>
      );
    }

    return (
      <div className="flex-1">
        <p className="text-center font-semibold text-sm text-[#1C1C1A] mb-3">
          {MONTHS[month]} {year}
        </p>
        <div className="grid grid-cols-7 mb-1">
          {DAYS_SHORT.map(d => (
            <div key={d} className="w-9 h-7 flex items-center justify-center text-[0.58rem] text-[#9CA3AF] uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-0.5">{cells}</div>
      </div>
    );
  }

  const displayText = () => {
    if (checkIn && checkOut) return `${formatShort(checkIn)} – ${formatShort(checkOut)}`;
    if (checkIn) return `${formatShort(checkIn)} —`;
    return null;
  };

  return (
    <div ref={ref} className={`relative ${isCard ? "w-full" : "flex-[2]"}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          setOpen(o => !o);
          setSelecting(checkIn && !checkOut ? "end" : "start");
        }}
        className={
          isCard
            ? "w-full flex flex-col justify-center px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
            : "w-full flex flex-col justify-center px-5 py-2 rounded-full hover:bg-gray-50 transition-colors text-left"
        }
      >
        <span className={`font-semibold uppercase tracking-[0.18em] ${isCard ? "text-[0.6rem] text-[#6B6B65]" : "text-[0.55rem] text-[#1C1C1A]"}`}>
          {checkIn && checkOut ? "Dates" : checkIn ? "Select check-out" : isCard ? "Check-in — Check-out" : "Add dates"}
        </span>
        {!isCard && (
          <span className="text-sm font-light">
            {displayText()
              ? <span className="text-[#1C1C1A]">{displayText()}</span>
              : <span className="text-[#9CA3AF]">Check-in — Check-out</span>}
          </span>
        )}
        {isCard && displayText() && (
          <span className="text-sm text-[#1C1C1A] font-light mt-0.5">{displayText()}</span>
        )}
      </button>

      {/* Clear */}
      {(checkIn || checkOut) && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onChange({ checkIn: "", checkOut: "" }); setSelecting("start"); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#1C1C1A] transition-colors"
        >
          <X size={14} />
        </button>
      )}

      {/* Calendar popup */}
      {open && (
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 z-[200]"
          style={
            isCard
              ? { position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", width: isMobile ? "calc(100vw - 32px)" : "min(680px, 95vw)" }
              : isMobile
                ? { position: "fixed", left: "16px", right: "16px", bottom: "16px" }
                : { position: "fixed", left: "50%", transform: "translateX(-50%)", width: "min(680px, 95vw)", bottom: "clamp(80px, 12vh, 140px)" }
          }
        >
          <p className="text-center text-xs text-[#6B6B65] mb-4 tracking-wide">
            {selecting === "start" ? "Select check-in date" : "Now select your check-out date"}
          </p>

          <div className="flex items-start gap-2">
            <button type="button" onClick={prev} className="mt-6 p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0">
              <ChevronLeft size={16} />
            </button>

            <div className="flex-1 grid grid-cols-1 gap-6">
              {renderMonth(leftYear, leftMonth)}
              {!isMobile && renderMonth(right.year, right.month)}
            </div>

            <button type="button" onClick={next} className="mt-6 p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
